import { sans } from '../Vendors/VendorDetails';
import { VendorLogo } from '../VendorRequests/VendorRequests';
import {
  RemediationSummary, RiskSeverity, SEVERITY_COLOR, STATUS_META, StatusPill, daysUntil, downloadCsv, fmtDate, palette, relative, scoreColor,
} from './remediationShared';

const SEVERITIES: RiskSeverity[] = ['Critical', 'High', 'Medium'];

export const exportRequests = (rows: RemediationSummary[], file: string) => downloadCsv(file,
  ['Reference', 'Request', 'Vendor', 'Domain', 'Status', 'Open critical', 'Open high', 'Open medium', 'Fixed', 'Total risks', 'Categories',
    'Score at request', 'Score now', 'Projected', 'Due', 'Requested by', 'Requested', 'Submitted', 'Completed', 'Contacts', 'Last activity'],
  rows.map(r => [r.reference, r.title, r.vendor_name, r.vendor_domain, STATUS_META[r.status].label, r.open_by_severity?.Critical ?? 0,
    r.open_by_severity?.High ?? 0, r.open_by_severity?.Medium ?? 0, r.fixed_risk_count, r.risk_count, r.categories.join('; '),
    r.initial_score ?? '', r.current_score ?? '', r.projected_score ?? '', r.due_date ?? '', r.created_by_name ?? '', fmtDate(r.created_at),
    fmtDate(r.submitted_at), fmtDate(r.completed_at), r.recipients.join('; '), fmtDate(r.updated_at)]));

/** Open risks of a request as coloured counts ("● 2  ● 1"), or "All fixed". */
export function OpenRiskCounts({ r, isDark }: { r: RemediationSummary; isDark: boolean }) {
  const c = palette(isDark);
  if (r.risk_count && !r.open_risk_count) return <span style={{ color: c.green, fontWeight: 600, whiteSpace: 'nowrap' }}>All fixed</span>;
  const parts = SEVERITIES.filter(s => (r.open_by_severity?.[s] ?? 0) > 0);
  if (!parts.length) return <span style={{ color: c.muted }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', gap: 10, whiteSpace: 'nowrap' }}
      title={parts.map(s => `${r.open_by_severity[s]} ${s.toLowerCase()}`).join(', ')}>
      {parts.map(s => {
        const color = isDark ? SEVERITY_COLOR[s].dark : SEVERITY_COLOR[s].light;
        return (
          <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontWeight: 600, color }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />{r.open_by_severity[s]} {s}
          </span>
        );
      })}
    </span>
  );
}

export function DueCell({ r, isDark }: { r: RemediationSummary; isDark: boolean }) {
  const c = palette(isDark);
  if (!r.due_date) return <span style={{ color: c.muted, whiteSpace: 'nowrap' }}>No due date</span>;
  if (r.status !== 'in_progress') return <span style={{ color: c.slate, whiteSpace: 'nowrap' }}>{fmtDate(r.due_date)}</span>;
  const left = daysUntil(r.due_date);
  return (
    <div style={{ whiteSpace: 'nowrap' }}>
      <div style={{ color: left < 0 ? c.danger : c.ink, fontWeight: left < 0 ? 600 : 400 }}>{fmtDate(r.due_date)}</div>
      <div style={{ fontSize: 11, color: left < 0 ? c.danger : left <= 7 ? c.amber : c.muted, fontWeight: left <= 7 ? 600 : 400 }}>
        {left < 0 ? `${-left} day${left === -1 ? '' : 's'} overdue` : left === 0 ? 'Due today' : `${left} day${left === 1 ? '' : 's'} left`}
      </div>
    </div>
  );
}

/** What last happened on a request, for the "Activity" column. */
export function activityNote(r: RemediationSummary): { text: string; tone: 'vendor' | 'plain' } {
  if (r.status === 'awaiting_review') return { text: 'Vendor submitted for review', tone: 'vendor' };
  if (r.last_message_by === 'vendor') return { text: 'Vendor replied', tone: 'vendor' };
  if (r.last_reminder_at && (!r.last_message_at || r.last_reminder_at > r.last_message_at)) return { text: 'Reminder sent', tone: 'plain' };
  if (r.last_message_by === 'tenant') return { text: 'You messaged', tone: 'plain' };
  return { text: '', tone: 'plain' };
}

interface Props {
  rows: RemediationSummary[];
  isDark: boolean;
  showVendor: boolean;
  selectable: boolean;
  selected: Set<string>;
  onToggle: (id: string, on: boolean) => void;
  onToggleAll: (on: boolean) => void;
  onOpen: (r: RemediationSummary) => void;
  activeId?: string | null;
}

/** The remediation request table shared by the all-vendors Remediation page and a vendor's Remediation tab. */
export default function RemediationTable({ rows, isDark, showVendor, selectable, selected, onToggle, onToggleAll, onOpen, activeId }: Props) {
  const c = palette(isDark);
  const th: React.CSSProperties = {
    textAlign: 'left', fontSize: 9.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.muted,
    padding: '9px 14px', background: c.surface2, borderBottom: `1px solid ${c.line}`, whiteSpace: 'nowrap',
  };
  const td: React.CSSProperties = { fontSize: 12, padding: '11px 14px', borderBottom: `1px solid ${c.rowLine}`, verticalAlign: 'middle', color: c.ink };
  const allOn = rows.length > 0 && rows.every(r => selected.has(r.id));

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: showVendor ? 1000 : 860, fontFamily: sans }}>
        <thead>
          <tr>
            {selectable && (
              <th style={{ ...th, width: 34, paddingRight: 0 }}>
                <input type="checkbox" aria-label="Select all shown" checked={allOn} disabled={!rows.length} onChange={() => onToggleAll(!allOn)} />
              </th>
            )}
            <th style={th}>Request</th>
            {showVendor && <th style={th}>Vendor</th>}
            <th style={th}>Open risks</th>
            <th style={th}>Fixed</th>
            <th style={th}>Score</th>
            <th style={th}>Due</th>
            <th style={th}>Last activity</th>
            <th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const active = activeId === r.id;
            const note = activityNote(r);
            return (
              <tr key={r.id} data-remediation-row onClick={() => onOpen(r)} style={{ cursor: 'pointer', background: active ? c.selected : 'transparent' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = c.surface2; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                {selectable && (
                  <td style={{ ...td, paddingRight: 0 }} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${r.title}`} checked={selected.has(r.id)} onChange={e => onToggle(r.id, e.target.checked)} />
                  </td>
                )}
                <td style={td}>
                  <div style={{ fontWeight: 600, fontSize: 12.5, maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.title}>{r.title}</div>
                  <div style={{ fontSize: 11, color: c.muted, marginTop: 2, whiteSpace: 'nowrap' }}>
                    {r.reference} · {fmtDate(r.created_at)}{r.created_by_name ? ` · ${r.created_by_name}` : ''}
                  </div>
                </td>
                {showVendor && (
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 150 }}>
                      <VendorLogo name={r.vendor_name || '?'} domain={r.vendor_domain || ''} size={26} isDark={isDark} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 12.5, whiteSpace: 'nowrap' }}>{r.vendor_name}</div>
                        <div style={{ fontSize: 11, color: c.muted }}>{r.vendor_domain}</div>
                      </div>
                    </div>
                  </td>
                )}
                <td style={td}><OpenRiskCounts r={r} isDark={isDark} /></td>
                <td style={{ ...td, whiteSpace: 'nowrap', color: c.slate }}>{r.fixed_risk_count} of {r.risk_count}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <span style={{ fontWeight: 700, color: scoreColor(r.current_score, isDark) }}>{r.current_score ?? '—'}</span>
                  {r.initial_score != null && r.current_score != null && r.current_score !== r.initial_score && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: r.current_score > r.initial_score ? c.green : c.danger, marginLeft: 5 }}>
                      {r.current_score > r.initial_score ? '▲' : '▼'}{Math.abs(r.current_score - r.initial_score)}
                    </span>
                  )}
                  {r.projected_score != null && r.projected_score !== r.current_score && (
                    <span style={{ fontSize: 11, color: c.muted, marginLeft: 6 }}>→ {r.projected_score}</span>
                  )}
                </td>
                <td style={td}><DueCell r={r} isDark={isDark} /></td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  <div style={{ color: c.slate }}>{relative(r.updated_at)}</div>
                  {note.text && <div style={{ fontSize: 11, color: note.tone === 'vendor' ? c.accent : c.muted, fontWeight: note.tone === 'vendor' ? 600 : 400 }}>{note.text}</div>}
                </td>
                <td style={td}><StatusPill status={r.status} isDark={isDark} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
